import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import type { RefObject } from 'react'

import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react'

import * as React from 'react'
import { useIntl } from 'react-intl'
import cruiseGlossaryMessages from 'hooks/Cruise/cruiseGlossaryMessages'
import CruiseLinesSearchList from './CruiseLineSelectContentList'
import { useCruiseSearchInput } from '../CruiseSearchInputContext'
import FormattedCruiseLineSearchSummary from './FormattedCruiseLineSearchSummary'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'

export interface CruiseLineSelectDesktopRef {
  focus: () => void
}

interface Props {
  cruiseLineNames: Array<string>
  dropdownAnchorRef: RefObject<HTMLElement | null>
  onChange: (values: App.CruiseGlobalFilters) => void
  handlerSearchTerm: (e: React.ChangeEvent<HTMLInputElement>) => void
  handlerClearSearchTerm: () => void
  searchTerm: string
}

const CruiseLineSelectDesktop = forwardRef<CruiseLineSelectDesktopRef, Props>((props, ref) => {
  const {
    cruiseLineNames,
    onChange,
    dropdownAnchorRef,
    handlerSearchTerm,
    handlerClearSearchTerm,
    searchTerm,
  } = props
  const { isDesktopOpen, open, close } = useCruiseSearchInput()
  const intl = useIntl()
  const inputTriggerRef = useRef<HTMLInputElement>(null)
  const [showAll, setShowAll] = useState(false)
  const { cruiseLines } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const globalSearchState = useContext(GlobalSearchStateContext)

  const onApply = useCallback(() => {
    trackClientEvent({
      subject: 'filter_applied',
      action: 'applied',
      category: 'cruise_line_filter',
      type: 'interaction',
    })

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'filters' },
      sortContext: { value: 'recommended' },
      searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
      filterContexts: cruiseLines.map(cruiseLine => ({
        label: 'Cruise Line',
        type: 'cruise_line',
        value: cruiseLine,
      })),
    }))

    onChange({ cruiseLines })
    close()
  }, [onChange, cruiseLines, close, globalSearchState])

  const onClear = useCallback(() => {
    trackClientEvent({
      subject: 'filter_cleared',
      action: 'cleared',
      category: 'cruise_line_filter',
      type: 'interaction',
    })
    searchDispatch({ type: GlobalSearchStateActions.SET_CRUISE_LINES, cruiseLines: [] })
    onChange({ cruiseLines: [] })
    setShowAll(false)
    handlerClearSearchTerm()
  }, [handlerClearSearchTerm, onChange, searchDispatch])

  const onOpen = useCallback(() => {
    trackClientEvent({
      subject: 'filter_opened',
      action: 'opened',
      category: 'cruise_line_filter',
      type: 'interaction',
    })
    open()
  }, [open])

  const cruiseLineLabel = useMemo(() => {
    if (isDesktopOpen) return searchTerm

    const [firstName] = cruiseLineNames
    if (firstName) {
      const more = cruiseLineNames.length > 1 ? `+${cruiseLineNames.length - 1}` : ''
      return `${firstName} ${more}`.trim()
    }
    if (cruiseLines.length > 0) {
      return intl.formatMessage(
        {
          id: 'xt-cn06ue8NATDHAEEr5',
          defaultMessage: '{count, plural, one {# cruise line} other {# cruise lines}}',
          description:
            'Summary label on closed cruise line filter when facet names are not loaded yet but cruise lines are selected',
        },
        { count: cruiseLines.length },
      )
    }
    return ''
  }, [cruiseLineNames, cruiseLines.length, intl, isDesktopOpen, searchTerm])

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputTriggerRef.current?.focus()
      open()
    },
  }), [open])

  const cruiseLinesLabel = intl.formatMessage({
    id: '9FdWIO4Y_3a5V422EHkU',
    defaultMessage: 'Cruise lines',
    description: 'Label for cruise lines filter input in the cruise offer or booking section',
  })

  const searchCruiseLinesPlaceholder = intl.formatMessage({
    id: 'yu-x9cjn8Wt2HeAObpNI',
    defaultMessage: 'Search cruise lines',
    description: 'Placeholder text when searching for cruise lines in the cruises section',
  })

  const allCruiseLinesPlaceholder = intl.formatMessage(
    cruiseGlossaryMessages.allCruiseLines,
  )

  const applyButtonText = intl.formatMessage({
    id: 'TNJVKbQiGBwo7e-dXWhG',
    defaultMessage: 'Apply',
    description: 'Apply button text for cruise line filter in the cruise section',
  })

  const resetAllButtonText = intl.formatMessage({
    id: 'sCFHYdWIGKVuJiLi-Vdc',
    defaultMessage: 'Reset all',
    description: 'Reset all button text for cruise line filter in the cruise section',
  })

  return (
    <div data-testid="cruise-line-select-desktop">
      <SearchFormTextField
        onClick={onOpen}
        ref={inputTriggerRef}
        label={cruiseLinesLabel}
        placeholder={isDesktopOpen ? searchCruiseLinesPlaceholder : allCruiseLinesPlaceholder}
        value={cruiseLineLabel}
        displayValue={cruiseLineLabel}
        onChange={handlerSearchTerm}
        onClear={handlerClearSearchTerm}
      />

      <DropdownSheet
        size="fill-anchor"
        data-testid="cruise-line-select-dropdown"
        anchorRef={dropdownAnchorRef}
        triggerRef={inputTriggerRef}
        open={isDesktopOpen}
        onClose={close}
        primaryActionProps={{
          'data-testid': 'cruise-filter-footer-apply',
          children: applyButtonText,
          onClick: onApply,
        }}
        secondaryActionProps={{
          'data-testid': 'cruise-filter-footer-clear-all',
          kind: 'tertiary',
          children: resetAllButtonText,
          onClick: onClear,
        }}
        footerStart={<FormattedCruiseLineSearchSummary />}
      >
        <CruiseLinesSearchList
          showAll={showAll}
          onSetShowAll={setShowAll}
          searchTerm={searchTerm}
        />
      </DropdownSheet>
    </div>
  )
})

CruiseLineSelectDesktop.displayName = 'CruiseLineSelectDesktop'

export default CruiseLineSelectDesktop
